import React, { useEffect, useState } from "react";
import Button from "../../../form/Button";
import Textarea from "../../../form/Textarea";
import { useMutation } from "@tanstack/react-query";
import { organizationAddEdit } from "../../../../services/organizationService";
import { toast } from "react-toastify";
import Input from "../../../form/Input";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import LangSwitch from "../../../elements/LangSwitch";

const BannedEmailsSettings = ({
  data = {},
  organizationDetailsRefetch = () => {},
}) => {
  const { t } = useTranslation();
  const [bannedEmails, setBannedEmails] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const { lang } = useSelector((state) => state?.langProvider);
  const [selectedLang, setSelectedLang] = useState(lang || "en");

  useEffect(() => {
    setBannedEmails(data?.setting?.bannedEmail?.join(", "));
    setErrorMessage(data?.setting?.bannedEmailMessage?.[selectedLang]);
  }, [data?.setting, selectedLang]);

  const { mutate: handleSave, isPending } = useMutation({
    mutationFn: async (value) => {
      const emailList = bannedEmails
        .split(/[\s,]+/)
        .map((email) => email.trim())
        .filter((email) => email.length > 0);

      const res = await organizationAddEdit({
        ...(data?._id && { id: data?._id }),
        requestType: "application/json",
        setting: {
          bannedEmail: emailList || [],
          bannedEmailMessage: {
            ...data?.setting?.bannedEmailMessage,
            [selectedLang]: errorMessage,
          },
        },
      });
      return res;
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        organizationDetailsRefetch();
      } else {
        toast.error(res?.message || res?.error);
      }
    },
  });

  return (
    <div className="mx-auto p-4 bg-white shadow rounded-md">
      <div className="pb-4">
        <LangSwitch
          langs={["en", "es"]}
          selectedLang={selectedLang}
          setSelectedLang={setSelectedLang}
        />
      </div>
      <div className="py-4 md:py-8 px-3 md:px-6 rounded-md space-y-4 border border-gray-200">
        <h2 className="text-xl md:text-2xl text-deep-purple font-bold font-Montserrat mb-4">
          {t("Banned email addresses")}
        </h2>

        <div className="border rounded-lg">
          <div className="bg-primary/10 border-l-4 border-primary text-red-800 p-4 rounded-t-lg md:mb-6 text-sm font-medium">
            <p>
              {t(
                "Email addresses listed here will be blocked from making purchases."
              )}
            </p>
          </div>

          <div className="p-4">
            <Textarea
              label={t(
                "Banned Email Addresses - Enter one email address per line."
              )}
              inputValue={bannedEmails || ""}
              onChange={(e) => setBannedEmails(e.target.value)}
              inputPlaceholder="example@email.com"
              rows={5}
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 text-deep-purple !font-medium !bg-surface-04 !border-surface-05"
            ></Textarea>
          </div>

          <div className="px-4 pb-4">
            <Input
              label={t(
                "Error message customers will see when entering a banned email address"
              )}
              type="text"
              value={errorMessage || ""}
              onChange={(e) => setErrorMessage(e.target.value)}
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
              inputClasses={
                "text-deep-purple !font-medium !h-12 !bg-surface-04 !border-surface-05"
              }
            />
          </div>
        </div>
      </div>
      <div className="my-6 flex justify-end bg-magnolia py-4 sticky bottom-0 right-0 z-[2] pr-4 md:pr-6">
        <Button
          buttonFunction={handleSave}
          className="bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-2 rounded-full transition"
          buttonLabel={t("Save")}
          {...(isPending && {
            loading: true,
            buttonIconPosition: "left",
            isDisable: true,
          })}
        />
      </div>
    </div>
  );
};

export default BannedEmailsSettings;
